import { z } from 'zod'
import ApiKeyField from 'app/frontend/base/components/form-fields/ApiKeyField'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import DestructiveField from 'app/frontend/base/components/form-fields/DestructiveField'
import { Trans, useTranslation } from 'react-i18next'
import { useUpdateTeam } from 'app/frontend/base/config/mutations'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'

import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { gameVersionToSubdomain } from 'app/frontend/types/custom/GameVersion'
dayjs.extend(RelativeTime)

const formSchema = z.object({
  name: z.string().min(1).max(20),
  description: z.string().optional().or(z.null()),
  readonlyKey: z.string().optional(),
  privateKey: z.string().optional(),
  spreadsheetUrl: z.string().optional().or(z.null()),
  default: z.boolean(),
  visibleInGuild: z.boolean(),
  orderInGuild: z.number(),
})

const TeamSection = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { mutate, isPending } = useUpdateTeam(team)
  const navigate = useNavigate()
  const {
    data: { gameVersion },
  } = usePageInfo()

  return (
    <>
      <FormWrapper
        key={team?.id}
        formSchema={formSchema}
        entityType='team'
        entity={team}
        onSave={(form, data) => {
          form.reset(data)

          // If the team was renamed, we should redirect to the new path (resetting all state)
          setTimeout(() => {
            if (data.path && data.path !== team.path) {
              window.location.href = `${data.path}/settings#team`
            }
          }, 100)
        }}
      >
        <TextField name='name' />

        <TextField
          name='spreadsheetUrl'
          type='url'
          customDescription={
            <Trans
              i18nKey='forms.fields.team.spreadsheetUrl.description'
              components={[
                <a
                  key='copy'
                  className='text-primary hover:text-primary/90'
                  href={`https://${gameVersionToSubdomain[gameVersion]}wowaudit.com/copy`}
                  target='_blank'
                  rel='noreferrer'
                />,
              ]}
            />
          }
          pattern='^https://docs\.google\.com/spreadsheets/d/[A-Za-z0-9_\-]+(?:/.*)?$'
        />

        <div>
          <ApiKeyField name='readonlyKey' />
          <div className='mb-2 flex justify-end'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => {
                localStorage.setItem(
                  'apiDocumentationSelectedTeamId',
                  team.id.toString(),
                )
                navigate('/api')
              }}
            >
              API documentation
            </Button>
          </div>
          <ApiKeyField name='privateKey' />
        </div>

        <CheckboxField name='default' />

        <TextField name='description' isMulti />

        <CheckboxField name='visibleInGuild' />

        <NumberField name='orderInGuild' />
      </FormWrapper>

      <h5 className='mt-12'>Danger zone</h5>

      <DestructiveField
        className='mt-4'
        label={t('forms.fields.team.deleteTeam.label')}
        confirmation={t('forms.fields.team.deleteTeam.confirmation')}
        description={
          team.markedForDeletionAt
            ? t('forms.fields.team.deleteTeam.inProgressDescription', {
                timeLeft: dayjs(team.markedForDeletionAt)
                  .add(3, 'day')
                  .fromNow(),
              })
            : t('forms.fields.team.deleteTeam.description')
        }
        onDelete={() =>
          mutate({ markedForDeletionAt: new Date().toISOString() })
        }
        onRevert={() => mutate({ markedForDeletionAt: null })}
        pendingUntil={team.markedForDeletionAt}
        loading={isPending}
      />
    </>
  )
}

export default TeamSection
